"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  hemisphereLetter,
  splitDdm,
  splitDms,
} from "@/lib/tool-kit/earth-science/coordinate-format";
import {
  type CoordinateAxis,
  parseCoordinate,
} from "@/lib/tool-kit/earth-science/coordinates";
import { parseScientificInput } from "@/lib/tool-kit/earth-science/format";
import {
  centralMeridian,
  fromUtm,
  toUtm,
  UTM_MAX_LATITUDE,
  UTM_MIN_LATITUDE,
  type Utm,
} from "@/lib/tool-kit/earth-science/utm";

/** English strings. A translated content file overrides these through `ui`. */
export const COORDINATE_CONVERTER_STRINGS = {
  modeLabel: "Convert from",
  modeLatLon: "Latitude and longitude",
  modeUtm: "UTM coordinates",
  precisionLabel: "Precision",
  precisionNormal: "Normal (about 0.1 m)",
  precisionHigh: "High (about 1 mm)",
  latLonHint:
    "Decimal degrees (48.8582), degrees and decimal minutes (48° 51.492′ N) or degrees, minutes and seconds (48°51′29.5″ N). South and west are negative, or end with S or W.",
  latitude: "Latitude",
  longitude: "Longitude",
  zone: "UTM zone (1–60)",
  hemisphereLabel: "Hemisphere",
  north: "North",
  south: "South",
  easting: "Easting (m)",
  northing: "Northing (m)",
  results: "Results",
  tableHeading: "Latitude and longitude in every format",
  columnFormat: "Format",
  columnLatitude: "Latitude",
  columnLongitude: "Longitude",
  rowDecimal: "Decimal degrees (DD)",
  rowHemisphere: "Decimal degrees with hemisphere",
  rowDdm: "Degrees, decimal minutes (DDM)",
  rowDms: "Degrees, minutes, seconds (DMS)",
  utmHeading: "UTM (WGS84)",
  utmZone: "Zone and band",
  utmHemisphere: "Hemisphere",
  utmEasting: "Easting (m)",
  utmNorthing: "Northing (m)",
  utmMeridian: "Central meridian of the zone",
  utmText: "UTM grid reference",
  exceptionNote:
    "This point falls in one of the zones that UTM adjusts: zone 32V is widened over south-west Norway, and band X uses zones 31, 33, 35 and 37 around Svalbard. The zone number differs from the plain 6° rule.",
  outsideUtm:
    "UTM covers 80° S to 84° N. Beyond those latitudes the polar areas use the Universal Polar Stereographic (UPS) grid, which this tool does not convert.",
  farFromMeridian:
    "This point is more than 3.5° from the zone's central meridian, so it lies outside the zone proper. The conversion is still exact, but the usual UTM zone for this longitude is different.",
  copyDecimal: "Copy decimal degrees",
  copyUtm: "Copy UTM",
  copied: "Copied!",
  enterValues: "Enter a latitude and longitude to convert.",
  enterUtm: "Enter a zone, easting and northing to convert.",
  problem: "{field} {message}",
  fieldLat: "Latitude",
  fieldLon: "Longitude",
  zoneInvalid: "The zone must be a whole number from 1 to 60.",
  eastingInvalid:
    "The easting must be a number of metres, normally between 100 000 and 900 000.",
  northingInvalid:
    "The northing must be a number of metres from 0 to 10 000 000 (up to 9 400 000 in the north).",
  latitudeOutOfUtm:
    "That easting and northing lie outside UTM's latitude range (80° S to 84° N). Check the zone, hemisphere and digits.",
  errors: {
    empty: "needs a value.",
    format:
      "could not be read. Use decimal degrees or degrees, minutes and seconds.",
    range: "is out of range (latitude ±90°, longitude ±180°).",
    minutes:
      "has minutes that must be below 60 (and whole when seconds follow).",
    seconds: "has seconds that must be below 60.",
    hemisphere:
      "has both a minus sign and a hemisphere letter. Use one or the other.",
    axis: "uses the wrong hemisphere letter (N or S for latitude, E or W for longitude).",
  },
} as const;

type Strings = typeof COORDINATE_CONVERTER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type Mode = "latlon" | "utm";
type Precision = "normal" | "high";

const PRECISIONS: Record<
  Precision,
  { dd: number; ddm: number; dms: number; metres: number }
> = {
  normal: { dd: 6, ddm: 4, dms: 2, metres: 2 },
  high: { dd: 8, ddm: 6, dms: 4, metres: 3 },
};

function fixed(fmt: Fmt, value: number, digits: number): string {
  return fmt(value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  });
}

interface Row {
  label: string;
  lat: string;
  lon: string;
}

function formatRows(
  strings: Strings,
  fmt: Fmt,
  point: { lat: number; lon: number },
  precision: Precision
): Row[] {
  const p = PRECISIONS[precision];
  const each = (write: (value: number, axis: CoordinateAxis) => string) => ({
    lat: write(point.lat, "lat"),
    lon: write(point.lon, "lon"),
  });
  return [
    {
      label: strings.rowDecimal,
      ...each((value) => fixed(fmt, value, p.dd)),
    },
    {
      label: strings.rowHemisphere,
      ...each(
        (value, axis) =>
          `${fixed(fmt, Math.abs(value), p.dd)}° ${hemisphereLetter(value, axis)}`
      ),
    },
    {
      label: strings.rowDdm,
      ...each((value, axis) => {
        const { degrees, minutes } = splitDdm(value, p.ddm);
        return `${fmt(degrees)}° ${fixed(fmt, minutes, p.ddm)}′ ${hemisphereLetter(value, axis)}`;
      }),
    },
    {
      label: strings.rowDms,
      ...each((value, axis) => {
        const { degrees, minutes, seconds } = splitDms(value, p.dms);
        return `${fmt(degrees)}° ${fmt(minutes)}′ ${fixed(fmt, seconds, p.dms)}″ ${hemisphereLetter(value, axis)}`;
      }),
    },
  ];
}

function utmText(fmt: Fmt, utm: Utm, digits: number): string {
  return `${utm.zone}${utm.band} ${fixed(fmt, utm.easting, digits)} ${fixed(fmt, utm.northing, digits)}`;
}

function FormatTable({
  strings,
  fmt,
  point,
  precision,
}: {
  strings: Strings;
  fmt: Fmt;
  point: { lat: number; lon: number };
  precision: Precision;
}) {
  const p = PRECISIONS[precision];
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.columnFormat,
          strings.columnLatitude,
          strings.columnLongitude,
        ]}
        rows={formatRows(strings, fmt, point, precision).map((row) => [
          row.label,
          row.lat,
          row.lon,
        ])}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyDecimal}
        small
        text={`${point.lat.toFixed(p.dd)}, ${point.lon.toFixed(p.dd)}`}
      />
    </>
  );
}

function UtmCards({
  strings,
  fmt,
  utm,
  precision,
}: {
  strings: Strings;
  fmt: Fmt;
  utm: Utm;
  precision: Precision;
}) {
  const digits = PRECISIONS[precision].metres;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.utmHeading}</h3>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.utmZone}
          value={`${utm.zone}${utm.band}`}
        />
        <StatCard
          label={strings.utmHemisphere}
          value={utm.hemisphere === "N" ? strings.north : strings.south}
        />
        <StatCard
          label={strings.utmMeridian}
          value={`${fmt(centralMeridian(utm.zone))}°`}
        />
        <StatCard
          label={strings.utmEasting}
          value={fixed(fmt, utm.easting, digits)}
        />
        <StatCard
          label={strings.utmNorthing}
          value={fixed(fmt, utm.northing, digits)}
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        <strong>{strings.utmText}:</strong> {utmText(fmt, utm, digits)}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyUtm}
        small
        text={`${utm.zone}${utm.band} ${utm.easting.toFixed(digits)} ${utm.northing.toFixed(digits)}`}
      />
      {utm.exception ? <p className={HINT}>{strings.exceptionNote}</p> : null}
    </>
  );
}

function LatLonOutcome({
  lat,
  lon,
  precision,
  strings,
  fmt,
}: {
  lat: string;
  lon: string;
  precision: Precision;
  strings: Strings;
  fmt: Fmt;
}) {
  const a = parseCoordinate(lat, "lat");
  const b = parseCoordinate(lon, "lon");
  if (!(a.ok && b.ok)) {
    const [error, field] = a.ok
      ? [b.ok ? "empty" : b.error, strings.fieldLon]
      : [a.error, strings.fieldLat];
    return error === "empty" ? (
      <Notice>{strings.enterValues}</Notice>
    ) : (
      <ErrorNotice>
        {t(strings.problem, { field, message: strings.errors[error] })}
      </ErrorNotice>
    );
  }
  const point = { lat: a.value, lon: b.value };
  const utm = toUtm(point.lat, point.lon);
  return (
    <>
      <FormatTable
        fmt={fmt}
        point={point}
        precision={precision}
        strings={strings}
      />
      {utm === null ? (
        <Notice>{strings.outsideUtm}</Notice>
      ) : (
        <UtmCards fmt={fmt} precision={precision} strings={strings} utm={utm} />
      )}
    </>
  );
}

interface UtmEntries {
  zone: string;
  easting: string;
  northing: string;
}

function UtmOutcome({
  entries,
  hemisphere,
  precision,
  strings,
  fmt,
}: {
  entries: UtmEntries;
  hemisphere: "N" | "S";
  precision: Precision;
  strings: Strings;
  fmt: Fmt;
}) {
  const zone = parseScientificInput(entries.zone);
  const easting = parseScientificInput(entries.easting);
  const northing = parseScientificInput(entries.northing);
  if (zone === null || easting === null || northing === null) {
    return <Notice>{strings.enterUtm}</Notice>;
  }
  if (!Number.isInteger(zone) || zone < 1 || zone > 60) {
    return <ErrorNotice>{strings.zoneInvalid}</ErrorNotice>;
  }
  if (easting < 0 || easting > 1_000_000) {
    return <ErrorNotice>{strings.eastingInvalid}</ErrorNotice>;
  }
  if (northing < 0 || northing > 10_000_000) {
    return <ErrorNotice>{strings.northingInvalid}</ErrorNotice>;
  }
  const point = fromUtm({ zone, hemisphere, easting, northing });
  if (
    point.lat < UTM_MIN_LATITUDE - 0.5 ||
    point.lat > UTM_MAX_LATITUDE + 0.5
  ) {
    return <ErrorNotice>{strings.latitudeOutOfUtm}</ErrorNotice>;
  }
  const away = Math.abs(
    ((point.lon - centralMeridian(zone) + 540) % 360) - 180
  );
  return (
    <>
      {away > 3.5 ? <Notice>{strings.farFromMeridian}</Notice> : null}
      <FormatTable
        fmt={fmt}
        point={point}
        precision={precision}
        strings={strings}
      />
    </>
  );
}

export default function CoordinateConverterWidget() {
  const strings = useToolUi(COORDINATE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("latlon");
  const [precision, setPrecision] = useState<Precision>("normal");
  const [lat, setLat] = useState("48.8582");
  const [lon, setLon] = useState("2.2945");
  const [hemisphere, setHemisphere] = useState<"N" | "S">("N");
  const [utm, setUtm] = useState<UtmEntries>({
    zone: "31",
    easting: "448251.795",
    northing: "5411932.678",
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.modeLabel}
          name="gcc-mode"
          onChange={setMode}
          options={[
            { value: "latlon", label: strings.modeLatLon },
            { value: "utm", label: strings.modeUtm },
          ]}
          value={mode}
        />
        <SelectField
          id="gcc-precision"
          label={strings.precisionLabel}
          onChange={setPrecision}
          options={[
            { value: "normal", label: strings.precisionNormal },
            { value: "high", label: strings.precisionHigh },
          ]}
          value={precision}
        />
      </div>
      {mode === "latlon" ? (
        <div className="space-y-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="gcc-lat"
              label={strings.latitude}
              numeric
              onChange={setLat}
              value={lat}
            />
            <TextField
              id="gcc-lon"
              label={strings.longitude}
              numeric
              onChange={setLon}
              value={lon}
            />
          </div>
          <p className={HINT}>{strings.latLonHint}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="gcc-zone"
            label={strings.zone}
            numeric
            onChange={(zone) => setUtm({ ...utm, zone })}
            value={utm.zone}
          />
          <PillGroup
            label={strings.hemisphereLabel}
            name="gcc-hemisphere"
            onChange={setHemisphere}
            options={[
              { value: "N", label: strings.north },
              { value: "S", label: strings.south },
            ]}
            value={hemisphere}
          />
          <TextField
            id="gcc-easting"
            label={strings.easting}
            numeric
            onChange={(easting) => setUtm({ ...utm, easting })}
            value={utm.easting}
          />
          <TextField
            id="gcc-northing"
            label={strings.northing}
            numeric
            onChange={(northing) => setUtm({ ...utm, northing })}
            value={utm.northing}
          />
        </div>
      )}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "latlon" ? (
          <LatLonOutcome
            fmt={fmt}
            lat={lat}
            lon={lon}
            precision={precision}
            strings={strings}
          />
        ) : (
          <UtmOutcome
            entries={utm}
            fmt={fmt}
            hemisphere={hemisphere}
            precision={precision}
            strings={strings}
          />
        )}
      </section>
    </div>
  );
}
